import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FaEye, FaEdit, FaTrash, FaSave, FaTimes, FaPlus, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import assetService from '../services/assetService';
import skillService from '../services/skillService';
import AssistantSidebar from '../components/AssistantSidebar';
import { API_ENDPOINTS } from '../config/api';
import '../styles/IssuesPage.css';

const STATUS_STEPS = ['pending', 'accepted', 'in_progress', 'resolved', 'closed'];
const STATUS_LABELS = {
  pending: 'Pending',
  accepted: 'Accepted',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
  rejected: 'Rejected'
};

const IssuesPage = ({ user }) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState('');
  const [assets, setAssets] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [skills, setSkills] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const token = sessionStorage.getItem('token');
  const navigate = useNavigate();
  const location = useLocation();

  const fetchIssues = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_ENDPOINTS.ISSUES, { headers: { Authorization: `Bearer ${token}` } });
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setIssues(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load issues');
    } finally { setLoading(false); }
  }, [token]);

  const fetchAssets = useCallback(async () => {
    try {
      const res = await assetService.getAllAssets();
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setAssets(data);
    } catch (err) { console.error('Failed to load assets', err); }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.DEPARTMENTS, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      const data = res.data?.data || res.data || [];
      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) { console.error('Failed to load departments', err); }
  }, [token]);

  const fetchSkills = useCallback(async () => {
    try {
      const res = await skillService.getAllSkills();
      const data = res?.data || res || [];
      setSkills(Array.isArray(data) ? data : (data.data || []));
    } catch (err) { console.error('Failed to load skills', err); }
  }, []);

  const fetchTechnicians = useCallback(async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.AUTH_TECHNICIANS, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      setTechnicians(res.data?.data || []);
    } catch (err) { console.error('Failed to load technicians', err); }
  }, [token]);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);

  // Auto-open issue if issueId is passed from BreakdownRequests or other referrer
  useEffect(() => {
    if (location.state?.issueId && issues.length > 0) {
      const issueToOpen = issues.find(issue => issue._id === location.state.issueId);
      if (issueToOpen) {
        openView(issueToOpen);
        // Clear the state to prevent re-opening on refresh
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state?.issueId, issues]);

  const getStatusIndex = (status) => {
    if (status === 'rejected') return -1;
    return STATUS_STEPS.indexOf(status);
  };

  const openView = (issue) => {
    setViewing(issue);
    setEditing(false);
  };

  const openEdit = (issue) => {
    setViewing(issue);
    setEditing(true);
    setEditForm({
      assetId: issue.assetId?._id || issue.assetId || '',
      department: issue.department?._id || issue.department || '',
      issueType: issue.issueType || 'breakdown',
      requiredSkill: issue.requiredSkill || '',
      assignedTechnician: issue.assignedTechnician?._id || issue.assignedTechnician || '',
      severity: issue.severity || 'medium',
      description: issue.description || '',
      checklist: Array.isArray(issue.checklist)
        ? issue.checklist.map(c => ({ text: c.text || c, completed: !!c.completed }))
        : []
    });
    setNewChecklistItem('');
    // Fetch dropdown data for edit form
    fetchAssets();
    fetchDepartments();
    fetchSkills();
    fetchTechnicians();
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API_ENDPOINTS.ISSUES}/${viewing._id}`, {
        assetId: editForm.assetId,
        department: editForm.department,
        issueType: editForm.issueType,
        requiredSkill: editForm.requiredSkill,
        assignedTechnician: editForm.assignedTechnician,
        severity: editForm.severity,
        description: editForm.description,
        checklist: editForm.checklist,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Issue updated successfully');
      setEditing(false);
      setViewing(null);
      fetchIssues();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update issue');
    }
  };

  const handleDelete = async (issueId) => {
    try {
      await axios.delete(`${API_ENDPOINTS.ISSUES}/${issueId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Issue deleted successfully');
      setDeleteConfirm(null);
      fetchIssues();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete issue');
    }
  };

  if (loading) return <div className="loading-state">Loading issues...</div>;

  return (
    <div className="issues-page-wrapper">
      <AssistantSidebar user={null} />
      <div className="issues-page">
      <div className="issues-header">
        <h2>All Issues</h2>
        <button className="btn-create" onClick={() => navigate('/issues/create')}>
          <FaPlus /> Create New Issue
        </button>
      </div>

      {error && <div className="error-state">{error}</div>}

      {issues.length === 0 && <div className="empty-state"><p>No issues created yet.</p></div>}

      <div className="issues-grid">
        {issues.map(issue => {
          const statusIdx = getStatusIndex(issue.status);
          const isRejected = issue.status === 'rejected';

          return (
            <div key={issue._id} className={`issue-card ${isRejected ? 'rejected-card' : ''}`}>
              {/* Status Progress Bar */}
              <div className="issue-status-bar">
                {isRejected ? (
                  <div className="status-rejected-bar">
                    <FaExclamationTriangle /> Rejected
                  </div>
                ) : (
                  <div className="status-steps">
                    {STATUS_STEPS.map((step, idx) => (
                      <div key={step} className={`status-step ${idx <= statusIdx ? 'completed' : ''} ${idx === statusIdx ? 'current' : ''}`}>
                        <div className="step-dot" />
                        {idx < STATUS_STEPS.length - 1 && <div className="step-line" />}
                        <span className="step-label">{STATUS_LABELS[step]}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Card content */}
              <div className="issue-card-content">
                <h3 className="issue-card-title">{issue.assetId?.assetName || 'Asset Issue'}</h3>

                <div className="issue-card-assigned">
                  <strong>Assigned:</strong> {issue.assignedTechnician?.name || '—'}
                  {issue.assignedTechnician && (
                    <span style={{
                      marginLeft: '8px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '10px',
                      fontWeight: '600',
                      backgroundColor: technicians.find(t => t._id === issue.assignedTechnician?._id)?.availability === 'busy' ? '#ffebee' : '#e8f5e9',
                      color: technicians.find(t => t._id === issue.assignedTechnician?._id)?.availability === 'busy' ? '#c62828' : '#2e7d32',
                      display: 'inline-block'
                    }}>
                      {technicians.find(t => t._id === issue.assignedTechnician?._id)?.availability === 'busy' ? '🔴 Busy' : '🟢 Available'}
                    </span>
                  )}
                </div>

                <p className="issue-card-description">{issue.description}</p>

                <div className="issue-card-meta">
                  <span className={`severity-tag ${issue.severity}`}>{issue.severity}</span>
                  <span className="type-tag">{issue.issueType}</span>
                  <span className="date-tag">Reported: {new Date(issue.reportedAt).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="issue-card-actions">
                <button className="action-btn view-btn" onClick={() => openView(issue)}>
                  <FaEye /> View
                </button>
                <button className="action-btn edit-btn" onClick={() => openEdit(issue)}>
                  <FaEdit /> Edit
                </button>
                <button className="action-btn delete-btn" onClick={() => setDeleteConfirm(issue._id)}>
                  <FaTrash /> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="issue-modal" onClick={() => setDeleteConfirm(null)}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-icon-wrap">
              <FaExclamationTriangle />
            </div>
            <h3>Delete Issue?</h3>
            <p>This action cannot be undone. The issue and all its data will be permanently removed.</p>
            <div className="delete-confirm-actions">
              <button className="btn-modal secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn-modal danger" onClick={() => handleDelete(deleteConfirm)}>
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View / Edit Modal */}
      {viewing && (
        <div className="issue-modal" onClick={() => { setViewing(null); setEditing(false); }}>
          <div className="modal-body" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal-header">
              <div className="modal-header-info">
                <h3>{viewing.assetId?.assetName || 'Issue Details'}</h3>
                <span className={`modal-status-badge ${viewing.status}`}>
                  {STATUS_LABELS[viewing.status] || viewing.status?.toUpperCase()}
                </span>
              </div>
              <button className="close-btn" onClick={() => { setViewing(null); setEditing(false); }}>
                <FaTimes />
              </button>
            </div>

            {/* Modal Status Bar */}
            <div className="modal-status-bar">
              {viewing.status === 'rejected' ? (
                <div className="status-rejected-bar modal-rejected">
                  <FaExclamationTriangle /> Issue was Rejected
                </div>
              ) : (
                <div className="status-steps modal-steps">
                  {STATUS_STEPS.map((step, idx) => {
                    const currentIdx = getStatusIndex(viewing.status);
                    return (
                      <div key={step} className={`status-step ${idx <= currentIdx ? 'completed' : ''} ${idx === currentIdx ? 'current' : ''}`}>
                        <div className="step-dot" />
                        {idx < STATUS_STEPS.length - 1 && <div className="step-line" />}
                        <span className="step-label">{STATUS_LABELS[step]}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="modal-content-scroll">
              {!editing ? (
                <>
                  <div className="modal-grid">
                    <div className="modal-detail-card">
                      <label>Asset</label>
                      <p>{viewing.assetId?.assetName || '—'}</p>
                    </div>
                    <div className="modal-detail-card">
                      <label>Serial Number</label>
                      <p>{viewing.assetId?.serialNumber || '—'}</p>
                    </div>
                    <div className="modal-detail-card">
                      <label>Location</label>
                      <p>{viewing.department?.name || '—'}</p>
                    </div>
                    <div className="modal-detail-card">
                      <label>Issue Type</label>
                      <p className="capitalize">{viewing.issueType}</p>
                    </div>
                    <div className="modal-detail-card">
                      <label>Severity</label>
                      <p><span className={`severity-tag ${viewing.severity}`}>{viewing.severity}</span></p>
                    </div>
                    <div className="modal-detail-card">
                      <label>Assigned To</label>
                      <p>
                        {viewing.assignedTechnician?.name || '—'}
                        {viewing.assignedTechnician && (
                          <span style={{
                            marginLeft: '8px',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            backgroundColor: technicians.find(t => t._id === viewing.assignedTechnician?._id)?.availability === 'busy' ? '#ffebee' : '#e8f5e9',
                            color: technicians.find(t => t._id === viewing.assignedTechnician?._id)?.availability === 'busy' ? '#c62828' : '#2e7d32'
                          }}>
                            {technicians.find(t => t._id === viewing.assignedTechnician?._id)?.availability === 'busy' ? '🔴 Busy' : '🟢 Available'}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="modal-section">
                    <h4>Description</h4>
                    <div className="description-box">{viewing.description}</div>
                  </div>

                  {viewing.requiredSkill && (
                    <div className="modal-section">
                      <h4>Required Skill</h4>
                      <span className="skill-tag">{viewing.requiredSkill}</span>
                    </div>
                  )}

                  {viewing.checklist && viewing.checklist.length > 0 && (
                    <div className="modal-section">
                      <h4>Checklist</h4>
                      <div className="checklist-list">
                        {viewing.checklist.map((item, idx) => (
                          <div key={idx} className={`checklist-item ${item.completed ? 'done' : ''}`}>
                            <span className="check-icon">{item.completed ? '✓' : '○'}</span>
                            <span>{item.text || item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {viewing.attachments && viewing.attachments.length > 0 && (
                    <div className="modal-section">
                      <h4>Attachments ({viewing.attachments.length})</h4>
                      <div className="attachments-grid">
                        {viewing.attachments.map((att, idx) => {
                          const isImage = att.mimetype && att.mimetype.startsWith('image/');
                          const isVideo = att.mimetype && att.mimetype.startsWith('video/');
                          return (
                            <div key={idx} className="attachment-card">
                              {isImage && att.data && (
                                <img src={att.data} alt={att.originalName} className="attachment-preview" />
                              )}
                              {isVideo && att.data && (
                                <video className="attachment-preview" controls>
                                  <source src={att.data} type={att.mimetype} />
                                </video>
                              )}
                              <div className="attachment-info">
                                <span className="attachment-name" title={att.originalName}>{att.originalName}</span>
                                <span className="attachment-size">{((att.size || 0) / 1024 / 1024).toFixed(2)} MB</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {viewing.technicianRemarks && (
                    <div className="modal-section">
                      <h4>Technician Remarks</h4>
                      <div className="description-box">{viewing.technicianRemarks}</div>
                    </div>
                  )}

                  {viewing.rejectionReason && (
                    <div className="modal-section">
                      <h4>Rejection Reason</h4>
                      <div className="description-box rejection">{viewing.rejectionReason}</div>
                    </div>
                  )}

                  <div className="modal-timestamps">
                    <div><strong>Reported:</strong> {new Date(viewing.reportedAt).toLocaleString()}</div>
                    {viewing.acceptedAt && <div><strong>Accepted:</strong> {new Date(viewing.acceptedAt).toLocaleString()}</div>}
                    {viewing.startedAt && <div><strong>Started:</strong> {new Date(viewing.startedAt).toLocaleString()}</div>}
                    {viewing.resolvedAt && <div><strong>Resolved:</strong> {new Date(viewing.resolvedAt).toLocaleString()}</div>}
                    {viewing.closedAt && <div><strong>Closed:</strong> {new Date(viewing.closedAt).toLocaleString()}</div>}
                  </div>
                </>
              ) : (
                /* Edit Form */
                <div className="edit-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Asset *</label>
                      <select value={editForm.assetId} onChange={(e) => setEditForm({ ...editForm, assetId: e.target.value })}>
                        <option value="">Select asset</option>
                        {assets.map(a => (
                          <option key={a._id} value={a._id}>{a.assetName} — {a.serialNumber || a._id}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Department *</label>
                      <select value={editForm.department} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}>
                        <option value="">Select Department</option>
                        {departments.map(dept => (
                          <option key={dept._id} value={dept._id}>{dept.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Issue Type *</label>
                      <select value={editForm.issueType} onChange={(e) => setEditForm({ ...editForm, issueType: e.target.value })}>
                        <option value="breakdown">Breakdown</option>
                        <option value="preventive">Preventive</option>
                        <option value="calibration">Calibration</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Severity *</label>
                      <select value={editForm.severity} onChange={(e) => setEditForm({ ...editForm, severity: e.target.value })}>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Required Skill *</label>
                      <select value={editForm.requiredSkill} onChange={(e) => setEditForm({ ...editForm, requiredSkill: e.target.value })}>
                        <option value="">Select skill</option>
                        {skills.map(s => (
                          <option key={s._id || s.name} value={s.name || s._id}>{s.label || s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Assign Technician *</label>
                      <select value={editForm.assignedTechnician} onChange={(e) => setEditForm({ ...editForm, assignedTechnician: e.target.value })}>
                        <option value="">Select technician</option>
                        {technicians.map(t => (
                          <option key={t._id} value={t._id}>
                            {t.name} — {t.email} [{t.availability === 'busy' ? '🔴 Busy' : '🟢 Available'}]
                          </option>
                        ))}
                      </select>
                      <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                        🟢 Available = No active issues | 🔴 Busy = Currently working on issue
                      </small>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Description *</label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      rows={4}
                      placeholder="Describe the issue..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Checklist</label>
                    <div className="checklist-edit-add">
                      <input
                        type="text"
                        placeholder="Add checklist item"
                        value={newChecklistItem}
                        onChange={(e) => setNewChecklistItem(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (newChecklistItem.trim()) {
                              setEditForm(prev => ({ ...prev, checklist: [...prev.checklist, { text: newChecklistItem.trim(), completed: false }] }));
                              setNewChecklistItem('');
                            }
                          }
                        }}
                      />
                      <button type="button" className="btn-add-checklist" onClick={() => {
                        if (newChecklistItem.trim()) {
                          setEditForm(prev => ({ ...prev, checklist: [...prev.checklist, { text: newChecklistItem.trim(), completed: false }] }));
                          setNewChecklistItem('');
                        }
                      }}>Add</button>
                    </div>
                    {editForm.checklist && editForm.checklist.length > 0 && (
                      <div className="checklist-list edit-checklist">
                        {editForm.checklist.map((item, idx) => (
                          <div key={idx} className={`checklist-item ${item.completed ? 'done' : ''}`}>
                            <input
                              type="checkbox"
                              checked={item.completed}
                              onChange={(e) => {
                                const newChecklist = [...editForm.checklist];
                                newChecklist[idx].completed = e.target.checked;
                                setEditForm({ ...editForm, checklist: newChecklist });
                              }}
                            />
                            <span>{item.text || item}</span>
                            <button type="button" className="checklist-remove-btn" onClick={() => {
                              setEditForm(prev => ({ ...prev, checklist: prev.checklist.filter((_, i) => i !== idx) }));
                            }}><FaTrash /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer buttons */}
            <div className="modal-footer">
              <button className="btn-modal secondary" onClick={() => { setViewing(null); setEditing(false); }}>
                {editing ? 'Cancel' : 'Close'}
              </button>
              {!editing && (
                <button className="btn-modal primary" onClick={() => openEdit(viewing)}>
                  <FaEdit /> Edit Issue
                </button>
              )}
              {editing && (
                <button className="btn-modal primary" onClick={saveEdit}>
                  <FaSave /> Save Changes
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default IssuesPage;
